/**
 * Modal component for viewing detailed document information
 * Shows document details, processing timeline, and extracted data
 * Supports both PDF and Excel document previews
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  EmailOutlined as EmailIcon,
  GetAppOutlined as DownloadIcon,
  CallSplitOutlined as SplitIcon,
  StorageOutlined as DataIcon,
  CloudUploadOutlined as UploadIcon,
  CheckCircleOutlined as CheckIcon,
  ChevronLeft,
  ChevronRight,
  CloudDownload,
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import * as XLSX from 'xlsx';
import axios from 'axios';

// Material-UI styles for the document modal layout and components
const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-container': {
      justifyContent: 'flex-end',
      alignItems: 'stretch',
    },
    '& .MuiDialog-paper': {
      margin: 0,
      maxWidth: '60vw',
      width: '60vw',
      maxHeight: '100vh',
      height: '100vh',
      borderRadius: 0,
      position: 'fixed',
      right: 0,
      top: 0,
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    '& .MuiTypography-h6': {
      display: 'flex',
      justifyContent: 'space-between',
      flexGrow: 1,
    },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#253239',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#666',
    marginTop: '2px',
  },
  closeButton: {
    color: '#666',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  content: {
    padding: '24px',
    height: 'calc(100vh - 80px)',
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#253239',
    marginBottom: '16px',
  },
  infoCard: {
    marginBottom: '0px',
    border: '0px solid #e0e0e0',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  infoGrid: {
    padding: '0px',

    '& .MuiCardContent-root': {
      padding: 0,
    },
  },
  infoLabel: {
    fontSize: '0.75rem',
    color: '#666',
    fontWeight: 500,
    marginBottom: '4px',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '0.95rem',
    color: '#253239',
    fontWeight: 500,
  },
  timelineCard: {
    marginBottom: '24px',
    border: '0px solid #e0e0e0',
    boxShadow: 'none',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  timelineTable: {
    '& .MuiTableHead-root': {
      backgroundColor: '#f8f9fa',
    },
  },
  timelineHeaderCell: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#253239',
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
  },
  timelineBodyCell: {
    padding: '16px 24px',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      // borderBottom: 'none',
    },
  },
  timelineStepContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  timelineStepText: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#253239',
  },
  timelineTimeText: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '2px',
  },
  timelineList: {
    padding: '16px 0',
  },
  timelineItem: {
    padding: '12px 24px',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  timelineIcon: {
    minWidth: '40px',
  },
  timelineText: {
    margin: 0,
  },
  timelinePrimary: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#253239',
  },
  timelineSecondary: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '2px',
  },
  successChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
    backgroundColor: '#28a745',
    color: '#ffffff',
  },
  processedChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
    backgroundColor: '#28a745',
    color: '#ffffff',
  },
  failedChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
    backgroundColor: '#dc3545',
    color: '#ffffff',
  },
  inProgressChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
  unprocessedChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '80px',
    backgroundColor: '#6c757d',
    color: '#ffffff',
  },
  extractedCard: {
    marginBottom: '24px',
    border: '0px solid #e0e0e0',
    backgroundColor: '#fff',
    borderRadius: '0px',
    boxShadow: 'none',
  },
  extractedGrid: {
    padding: '0px',
    // backgroundColor: '#f4f4f4',
  },
  viewPdfButton: {
    backgroundColor: '#FF6B35',
    color: '#fff',
    borderRadius: '40px',
    padding: '8px 24px',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#e55a2b',
    },
  },
  stepIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  emailIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  downloadIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  splitIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  dataIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  uploadIcon: {
    color: '#535862',
    fontSize: '20px',
  },
  vaultIcon: {
    color: '#535862',
    fontSize: '20px',
  },
});

const ViewDocumentModal = ({ classes, open, onClose, document }) => {
  // State for controlling the extracted data preview modal
  const [extractedDataOpen, setExtractedDataOpen] = useState(false);
  
  // State for Excel file data and loading
  const [excelData, setExcelData] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelPage, setExcelPage] = useState(0);
  
  // Loads and parses Excel files from S3 URLs
  const loadExcelFile = async (s3Url) => {
    if (!s3Url) return;
    
    setExcelLoading(true);
    
    try {
      // Attempt direct fetch first, then fallback to axios if needed
      let response;
      try {
        response = await fetch(s3Url, {
          method: 'GET',
          mode: 'cors',
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Parse Excel file using XLSX library
        const arrayBuffer = await response.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        setExcelData(jsonData);
        
      } catch (fetchError) {
        
        // Fallback method using axios
        response = await axios.get(s3Url, {
          responseType: 'arraybuffer',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,*/*'
          }
        });
        
        const data = new Uint8Array(response.data);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        setExcelData(jsonData);
      }
      
    } catch (error) {
      console.error('Error loading Excel file:', error);
      console.error('Failed URL:', s3Url);
      // Mark as error state for UI display
      setExcelData('error');
    } finally {
      setExcelLoading(false);
    }
  };

  // Load Excel data when modal opens, reset when it closes
  useEffect(() => {
    
    if (open && document && document.docFormat === 'excel' && document.s3Url) {
      loadExcelFile(document.s3Url);
    }
    // Clean up state when modal closes
    if (!open) {
      setExcelData(null);
      setExcelPage(0);
    }
  }, [open, document]);

  if (!document) return null;


  // Generate timeline status chips with color coding
  const getStatusChip = (status) => {
    // Normalize status values to consistent display format
    const transformedStatus = status === 'SUCCESS' ? 'Success' :
                             status === 'IN_PROGRESS' ? 'In Progress' :
                             status === 'FAILED' ? 'Failed' :
                             status === 'UNPROCESSED' ? 'Unprocessed' :
                             status === 'unprocessed' ? 'Unprocessed' :
                             status === 'processed' ? 'Success' :
                             status === 'processing' ? 'In Progress' :
                             status === 'queued' ? 'In Progress' :
                             status || 'N/A';
    
    // Apply appropriate styling based on status
    let chipClass = classes.successChip;
    
    switch (transformedStatus.toLowerCase()) {
      case 'success':
        chipClass = classes.processedChip;
        break;
      case 'failed':
      case 'error':
        chipClass = classes.failedChip;
        break;
      case 'in progress':
      case 'pending':
        chipClass = classes.inProgressChip;
        break;
      case 'unprocessed':
        chipClass = classes.unprocessedChip;
        break;
      default:
        chipClass = classes.successChip;
        break;
    }
    
    return <Chip label={transformedStatus} className={chipClass} size="small" />;
  };

  // Transform timeline data for display with appropriate icons and formatting
  const processTimeline = document.timeline ? document.timeline.map((timelineStep) => {
    let icon;
    
    // Assign icons based on processing stage
    switch (timelineStep.stage) {
      case 'EMAIL RECEIVED':
        icon = <EmailIcon className={classes.emailIcon} />;
        break;
      case 'ATTACHMENT DOWNLOADED':
        icon = <DownloadIcon className={classes.downloadIcon} />;
        break;
      case 'DOCUMENT SPLIT':
        icon = <SplitIcon className={classes.splitIcon} />;
        break;
      case 'DATA EXTRACTED':
        icon = <DataIcon className={classes.dataIcon} />;
        break;
      case 'VAULT RECORDED':
        icon = <CheckIcon className={classes.vaultIcon} />;
        break;
      default:
        icon = <CheckIcon className={classes.vaultIcon} />;
    }

    // Format timestamps for display
    // Normalize timestamps to UTC format by adding 'Z' if missing
    const normalizeTimestamp = (timestamp) => {
      if (!timestamp) return null;
      // If timestamp doesn't end with 'Z' and doesn't have timezone info, treat it as UTC
      if (typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
        return timestamp + 'Z';
      }
      return timestamp;
    };
    
    const startTime = new Date(normalizeTimestamp(timelineStep.startTime)).toLocaleString();
    const endTime = new Date(normalizeTimestamp(timelineStep.endTime)).toLocaleString();
    const timeDisplay = `Started: ${startTime}`;

    return {
      title: timelineStep.stage,
      time: timeDisplay,
      endTime: timelineStep.endTime,
      status: timelineStep.status === 'SUCCESS' ? 'Success' :
              timelineStep.status === 'IN_PROGRESS' ? 'In Progress' :
              timelineStep.status === 'FAILED' ? 'Failed' :
              timelineStep.status === 'UNPROCESSED' ? 'Unprocessed' :
              timelineStep.status || 'N/A',
      icon: icon,
    };
  }) : [];

  // Check if document has meaningful extracted data to show
  const hasExtractedData = document.extractedData && 
    Object.keys(document.extractedData).length > 0 && 
    Object.values(document.extractedData).some(value => value !== null && value !== undefined && value !== '');

  // Safely parse field values from various formats (strings, objects with Decimal values)
  const parseFieldValue = (fieldValue) => {
    if (!fieldValue) return 'N/A';
    
    try {
      // Handle string representations of objects with Decimal values
      if (typeof fieldValue === 'string' && fieldValue.includes("'value':")) {
        // Clean up Decimal formatting and parse as JSON
        const cleanedValue = fieldValue
          .replace(/Decimal\('([^']+)'\)/g, '"$1"')
          .replace(/'/g, '"');
        const parsed = JSON.parse(cleanedValue);
        return parsed.value || 'N/A';
      }
      // Handle objects with value property
      else if (typeof fieldValue === 'object' && fieldValue !== null && fieldValue.value !== undefined) {
        return fieldValue.value || 'N/A';
      }
      // Handle plain strings
      else if (typeof fieldValue === 'string') {
        return fieldValue;
      }
      return 'N/A';
    } catch (e) {
      console.warn('Failed to parse field value:', fieldValue, e);
      return 'N/A';
    }
  };

  // Extract and format data from document splits for display
  const extractedData = document.splits && document.splits.length > 0 && document.splits[0].extractedData ? {
    lotNo: parseFieldValue(document.splits[0].extractedData.batch_number),
    supplier: document.cmo || 'N/A',
    partNumber: parseFieldValue(document.splits[0].extractedData.cmo_product_code),
    quantity: parseFieldValue(document.splits[0].extractedData.ready_for_dispatch_quantity),
    certDate: parseFieldValue(document.splits[0].extractedData.cmo_invoice_date),
  } : null;

  // Check if VAULT RECORDED stage is completed with SUCCESS status
  const isVaultRecordedSuccess = document.timeline && document.timeline.some(
    (timelineStep) => timelineStep.stage === 'VAULT RECORDED' && timelineStep.status === 'SUCCESS'
  );

  // Show Sheet Summary only for Excel documents with successful VAULT RECORDED stage
  const shouldShowSheetSummary = document.docFormat === 'excel' && 
                                 isVaultRecordedSuccess && 
                                 document.sheetSummary && 
                                 Object.keys(document.sheetSummary).length > 0;

  // For non-Excel splits, check if sheetSummary exists (Batch format)
  const shouldShowBatchSummary = document.docFormat !== 'excel' && 
                                 document.splitId && 
                                 isVaultRecordedSuccess && 
                                 document.sheetSummary && 
                                 document.sheetSummary.Extract;

  // Format date to DD-MMM-YYYY HH:mm:ss format
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      console.warn('Failed to format date:', dateString, e);
      return 'N/A';
    }
  };

  // Extract batch summary data
  const getBatchSummaryData = () => {
    if (!shouldShowBatchSummary) return null;
    
    const extractData = document.sheetSummary.Extract;
    const results = extractData.api_response_summary?.results || [];
    
    // First result has file info, second has workflow info
    const fileInfo = results[0] || {};
    const workflowInfo = results[1] || {};
    
    return {
      fileId: workflowInfo.signalInput?.fileId || 'N/A',
      processFlowId: workflowInfo.processFlowId || 'N/A',
      lastUpdated: extractData.last_updated || 'N/A',
      workflowStatus: workflowInfo.workflowStatus || 'N/A',
      apiMessage: extractData.api_message || 'N/A',
      processedFileName: fileInfo.processedFileName || 'N/A',
    };
  };

  const batchSummaryData = getBatchSummaryData();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        className={classes.dialog}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          style: {
            transform: open ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 300ms ease-in-out',
          },
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div>
            <Typography className={classes.title}>
              Document Details
            </Typography>
            <Typography className={classes.subtitle}>
              {/* Show split filename if it's a split document, otherwise show original filename */}
              {document.splitId ? 
                (document.s3Key ? document.s3Key.split('/').pop() : document.splitId) :
                (document.originalFileName || document.fileName || '')
              }
            </Typography>
          </div>
          <IconButton onClick={onClose} className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Grid container spacing={3}>
            {/* Document Information - Full Width */}
            <Grid item xs={12}>
              <Typography className={classes.sectionTitle}>
                Document Information
              </Typography>
              
              <Card elevation={0} className={classes.infoCard}>
                <CardContent className={classes.infoGrid}>
                  <Grid container spacing={3} >
                    <Grid item xs={4}>
                      <Typography className={classes.infoLabel}>PO Number</Typography>
                      <Typography className={classes.infoValue}>
                        {document.poNumber || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography className={classes.infoLabel}>Batch Number</Typography>
                      <Typography className={classes.infoValue}>
                        {document.batch || document.batchNumber || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography className={classes.infoLabel}>Sender</Typography>
                      <Typography className={classes.infoValue}>
                        {document.senderEmail || document.sender || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Processing Timeline and Extracted Data - Side by Side */}
            <Grid item xs={12} md={7}>
              {/* Processing Timeline */}
              <Typography className={classes.sectionTitle}>
                Processing Timeline
              </Typography>

              <Card className={classes.timelineCard}>
                <TableContainer>
                  <Table className={classes.timelineTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.timelineHeaderCell}>Run Time</TableCell>
                        <TableCell className={classes.timelineHeaderCell}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processTimeline.map((step, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.timelineBodyCell}>
                            <div className={classes.timelineStepContainer}>
                              {step.icon}
                              <div>
                                <Typography className={classes.timelineStepText}>
                                  {step.title}
                                </Typography>
                                <Typography className={classes.timelineTimeText}>
                                  {step.time}
                                </Typography>
                                <Typography className={classes.timelineTimeText}>
                                  Finished: {new Date(processTimeline[index].endTime.endsWith?.('Z') || processTimeline[index].endTime.includes?.('+') || processTimeline[index].endTime.includes?.('-', 10) ? processTimeline[index].endTime : processTimeline[index].endTime + 'Z').toLocaleString()}
                                </Typography>
                                {/* {step.description && (
                                  <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#666', marginTop: '4px' }}>
                                    {step.description}
                                  </Typography>
                                )} */}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className={classes.timelineBodyCell} >
                            {getStatusChip(step.status)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={5}>
              {/* Extracted Data */}
              <Typography className={classes.sectionTitle}>
                Extracted Data
              </Typography>

              <Card className={classes.extractedCard}>
                <CardContent className={classes.extractedGrid}>
                  <Grid container spacing={2}>
                    <Grid item>
                      <Button 
                        className={classes.viewPdfButton}
                        // endIcon={<span style={{ fontSize: '1.2rem' }}>📄</span>}
                        fullWidth
                        disabled={!hasExtractedData}
                        onClick={() => setExtractedDataOpen(true)}
                        style={{
                          backgroundColor: hasExtractedData ? '#FF6B35' : '#cccccc',
                          color: hasExtractedData ? '#fff' : '#666',
                          cursor: hasExtractedData ? 'pointer' : 'not-allowed'
                        }}
                      >
                        {document.docFormat === 'excel' ? 'View Excel' : 'View PDF'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Sheet Summary Section - Only for Excel documents */}
            {shouldShowSheetSummary && (
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Sheet Summary (Excel)
                </Typography>

                {Object.entries(document.sheetSummary).map(([sheetName, sheetData], index) => (
                  <div key={index} style={{ marginBottom: index < Object.keys(document.sheetSummary).length - 1 ? '24px' : '0' }}>
                    <Typography 
                      variant="subtitle1" 
                      style={{ 
                        fontWeight: 600, 
                        fontSize: '1rem', 
                        color: '#253239',
                        marginBottom: '12px',
                        marginTop: index > 0 ? '16px' : '0'
                      }}
                    >
                      {sheetName}
                    </Typography>
                    
                    <Card elevation={0} className={classes.infoCard}>
                      <CardContent className={classes.infoGrid}>
                        <Grid container spacing={3}>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>Total Rows</Typography>
                            <Typography className={classes.infoValue}>
                              {sheetData.total_rows ?? 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>Skipped Count</Typography>
                            <Typography className={classes.infoValue}>
                              {sheetData.skipped_count ?? 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>Uploaded Count</Typography>
                            <Typography className={classes.infoValue}>
                              {sheetData.uploaded_count ?? 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>Failed Count</Typography>
                            <Typography className={classes.infoValue}>
                              {sheetData.failed_count ?? 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>Last Updated</Typography>
                            <Typography className={classes.infoValue}>
                              {formatDateTime(sheetData.last_updated)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography className={classes.infoLabel}>API Message</Typography>
                            <Typography className={classes.infoValue}>
                              {sheetData.api_message || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </Grid>
            )}

            {/* Sheet Summary Section - For non-Excel splits (Batch format) */}
            {shouldShowBatchSummary && batchSummaryData && (
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Sheet Summary (Batch)
                </Typography>

                <Card elevation={0} className={classes.infoCard}>
                  <CardContent className={classes.infoGrid}>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>File ID</Typography>
                        <Typography className={classes.infoValue} style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                          {batchSummaryData.fileId}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Process Flow ID</Typography>
                        <Typography className={classes.infoValue}>
                          {batchSummaryData.processFlowId}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Last Updated</Typography>
                        <Typography className={classes.infoValue}>
                          {formatDateTime(batchSummaryData.lastUpdated)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Workflow Status</Typography>
                        <Typography className={classes.infoValue}>
                          {batchSummaryData.workflowStatus}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography className={classes.infoLabel}>API Message</Typography>
                        <Typography className={classes.infoValue}>
                          {batchSummaryData.apiMessage}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Extracted Data Dialog */}
      <Dialog
        open={extractedDataOpen}
        onClose={() => setExtractedDataOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            maxHeight: '90vh',
            width: '90vw',
            maxWidth: 'none'
          }
        }}
      >
        <DialogTitle style={{ paddingBottom: '8px', borderBottom: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {document.docType || document.document_type || 'Document Details'}
            </Typography>
            <IconButton
              onClick={() => setExtractedDataOpen(false)}
              size="small"
              style={{ color: '#666' }}
            >
              ✕
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '20px' }}>
          <Grid container spacing={3}>
            {/* Left Panel - Extracted Data */}
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="h6" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                  {/* {document.docType || document.document_type || 'Extracted Data'} */}
                  Extracted Data
                </Typography>
              </div>
              
              <div style={{ 
                height: 'calc(80vh - 160px)', 
                overflowY: 'auto', 
                padding: '16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#fff'
              }}>
                {document.extractedData && Object.keys(document.extractedData).length > 0 ? (
                  <div>
                    {/* Simple fields */}
                    {Object.entries(document.extractedData).map(([key, value]) => {
                      // Skip complex nested objects - we'll handle them separately
                      if (key === 'bowls' || key === 'material_information' || 
                          (typeof value === 'object' && value !== null && !value.hasOwnProperty('value') && typeof value !== 'string')) {
                        return null;
                      }
                      
                      // Extract value from confidence objects
                      let displayValue = 'N/A';
                      let confidence = null;
                      
                      try {
                        if (typeof value === 'string' && value.includes("'value':")) {
                          // Parse the string representation of the object, handling Decimal values
                          const parsed = value
                            .replace(/Decimal\('([^']+)'\)/g, '"$1"')
                            .replace(/'/g, '"');
                          const valueObj = JSON.parse(parsed);
                          displayValue = valueObj.value || 'N/A';
                          confidence = valueObj.confidence;
                        } else if (typeof value === 'object' && value !== null && value.value !== undefined) {
                          displayValue = value.value || 'N/A';
                          confidence = value.confidence;
                        } else if (typeof value === 'string' && !value.includes("'value':")) {
                          displayValue = value || 'N/A';
                        }
                      } catch (e) {
                        console.warn('Failed to parse value:', value, e);
                        displayValue = 'N/A';
                      }
                      
                      // Format the key to be more readable
                      const formattedKey = key
                        .replace(/_/g, ' ')
                        .replace(/([a-z])([A-Z])/g, '$1 $2')
                        .replace(/\b\w/g, l => l.toUpperCase());
                      
                      // Get confidence color
                      const getConfidenceColor = (conf) => {
                        if (!conf) return '#cccccc';
                        if (conf >= 90) return 'green';
                        if (conf >= 80) return 'orange';
                        return 'red';
                      };
                      
                      return (
                        <div key={key} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                          <div style={{ width: '35%', paddingRight: '16px' }}>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {formattedKey}
                            </Typography>
                          </div>
                          <div style={{ width: '65%', display: 'flex', alignItems: 'center' }}>
                            <div style={{ 
                              border: '1px solid #e0e0e0', 
                              borderRadius: '4px', 
                              padding: '8px 12px',
                              flex: 1,
                              marginRight: '8px',
                              fontSize: '0.875rem',
                              backgroundColor: '#fafafa'
                            }}>
                              {displayValue}
                            </div>
                            {/* {confidence !== null && (
                              <div style={{
                                backgroundColor: getConfidenceColor(confidence),
                                color: 'white',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                minWidth: '40px',
                                textAlign: 'center'
                              }}>
                                {Math.round(confidence)}
                              </div>
                            )} */}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Complex nested data sections */}
                    {document.extractedData.bowls && (
                      <div style={{ marginTop: '24px' }}>
                        {/* <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px', color: '#333' }}>
                          Consumption Details by Bowl
                        </Typography> */}
                        {document.extractedData.bowls.map((bowlData, bowlIndex) => {
                          return (
                            <div key={bowlIndex} style={{ marginBottom: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}>
                              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '16px', color: '#1976d2' }}>
                                Bowl {bowlData.bowl?.value || bowlData.bowl || (bowlIndex + 1)}
                              </Typography>
                              
                              {/* Handle nested rows structure (Certificate of Manufacture format) */}
                              {bowlData.rows && bowlData.rows.map((row, rowIndex) => (
                                <div key={rowIndex} style={{ marginBottom: '16px' }}>
                                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: '12px', color: '#666' }}>
                                    Row {rowIndex + 1}
                                  </Typography>
                                  {Object.entries(row).map(([fieldKey, fieldValue]) => {
                                    if (fieldKey === 'row_id') return null;
                                    const formattedFieldKey = fieldKey
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, l => l.toUpperCase());
                                    
                                    // Get confidence color
                                    const getConfidenceColor = (conf) => {
                                      if (!conf) return '#cccccc';
                                      if (conf >= 90) return 'green';
                                      if (conf >= 80) return 'orange';
                                      return 'red';
                                    };
                                    
                                    return (
                                      <div key={fieldKey} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                                        <div style={{ width: '35%', paddingRight: '16px' }}>
                                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                                            {formattedFieldKey}
                                          </Typography>
                                        </div>
                                        <div style={{ width: '65%', display: 'flex', alignItems: 'center' }}>
                                          <div style={{ 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: '4px', 
                                            padding: '8px 12px',
                                            flex: 1,
                                            marginRight: '8px',
                                            fontSize: '0.875rem',
                                            backgroundColor: '#fafafa'
                                          }}>
                                            {fieldValue.value || 'N/A'}
                                          </div>
                                          {/* <div style={{
                                            backgroundColor: getConfidenceColor(fieldValue.confidence),
                                            color: 'white',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            minWidth: '40px',
                                            textAlign: 'center'
                                          }}>
                                            {fieldValue.confidence ? Math.round(fieldValue.confidence) : '0'}
                                          </div> */}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                              
                              {/* Handle flat structure (Consumption Details format) */}
                              {!bowlData.rows && (
                                <div>
                                  {Object.entries(bowlData).map(([fieldKey, fieldValue]) => {
                                    if (fieldKey === 'bowl') return null; // Skip bowl field as it's already shown in title
                                    
                                    // Check if fieldValue has value/confidence structure
                                    if (typeof fieldValue === 'object' && fieldValue !== null && 'value' in fieldValue) {
                                      const formattedFieldKey = fieldKey
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, l => l.toUpperCase());
                                      
                                      // Get confidence color
                                      const getConfidenceColor = (conf) => {
                                        if (!conf) return '#cccccc';
                                        if (conf >= 90) return 'green';
                                        if (conf >= 80) return 'orange';
                                        return 'red';
                                      };
                                      
                                      return (
                                        <div key={fieldKey} style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                                          <div style={{ width: '35%', paddingRight: '16px' }}>
                                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                                              {formattedFieldKey}
                                            </Typography>
                                          </div>
                                          <div style={{ width: '65%', display: 'flex', alignItems: 'center' }}>
                                            <div style={{ 
                                              border: '1px solid #e0e0e0', 
                                              borderRadius: '4px', 
                                              padding: '8px 12px',
                                              flex: 1,
                                              marginRight: '8px',
                                              fontSize: '0.875rem',
                                              backgroundColor: '#fafafa'
                                            }}>
                                              {fieldValue.value || 'N/A'}
                                            </div>
                                            {/* <div style={{
                                              backgroundColor: getConfidenceColor(fieldValue.confidence),
                                              color: 'white',
                                              borderRadius: '4px',
                                              padding: '4px 8px',
                                              fontSize: '0.75rem',
                                              fontWeight: 600,
                                              minWidth: '40px',
                                              textAlign: 'center'
                                            }}>
                                              {fieldValue.confidence ? Math.round(fieldValue.confidence) : '0'}
                                            </div> */}
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="body1" style={{ color: '#666', fontStyle: 'italic' }}>
                      No extracted data available
                    </Typography>
                  </div>
                )}
              </div>
            </Grid>
            
            {/* Right Panel - Document Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '16px' }}>
                {document.docFormat === 'excel' ? 'Excel Preview' : 'PDF Preview'}
              </Typography>
              <div style={{ 
                height: 'calc(80vh - 160px)', 
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {document.docFormat === 'excel' ? (
                  // Excel Preview
                  excelLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : excelData === 'error' ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <div style={{ textAlign: 'center' }}>
                        <Typography variant="body1" style={{ color: '#d32f2f', marginBottom: '8px' }}>
                          Failed to load Excel file
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          The file may not be accessible or there was a network error
                        </Typography>
                        {document.s3Url && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CloudDownload />}
                            href={document.s3Url}
                            target="_blank"
                            style={{ marginTop: '12px' }}
                          >
                            Try Direct Download
                          </Button>
                        )}
                      </div>
                    </Box>
                  ) : excelData ? (
                    <>
                      {/* Excel Header */}
                      <Box p={2} borderBottom="1px solid #e0e0e0" bgcolor="#fff">
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center">
                            <img 
                              src="https://img.icons8.com/color/96/000000/microsoft-excel-2019--v1.png" 
                              alt="Excel file" 
                              style={{ width: '32px', height: '32px', marginRight: '12px' }}
                            />
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                              Excel Spreadsheet
                            </Typography>
                          </Box>
                          {/* {document.s3Url && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<CloudDownload />}
                              href={document.s3Url}
                              target="_blank"
                            >
                              Download
                            </Button>
                          )} */}
                        </Box>
                      </Box>
                      
                      {/* Excel Table */}
                      <Box flex={1} overflow="hidden">
                        <TableContainer style={{ height: '100%', overflow: 'auto' }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {excelData[0] && excelData[0].map((cell, i) => (
                                  <TableCell key={i} style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.75rem' }}>
                                    {cell || `Column ${i + 1}`}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {excelData.slice(1, Math.min(101, excelData.length)).map((row, i) => (
                                <TableRow key={i}>
                                  {row.map((cell, j) => (
                                    <TableCell key={j} style={{ fontSize: '0.75rem' }}>
                                      {cell !== null && cell !== undefined ? String(cell) : ''}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                      
                      {excelData.length > 101 && (
                        <Box p={1} borderTop="1px solid #e0e0e0" bgcolor="#fff" textAlign="center">
                          <Typography variant="caption" color="textSecondary">
                            Showing first 100 rows of {excelData.length - 1} total rows
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Typography variant="body1" style={{ color: '#666' }}>
                        Excel preview not available
                      </Typography>
                    </Box>
                  )
                ) : (
                  // PDF Preview (placeholder)
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography variant="body1" style={{ color: '#666' }}>
                      {/* PDF preview functionality will be implemented */}
                    </Typography>
                  </Box>
                )}
              </div>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withStyles(styles)(ViewDocumentModal);